import Chart from 'chart.js/auto';
import {
    buildFilterSelects,
    attachTimeListeners,
    attachFilterListeners,
    getVisibleCombos,
    getActiveSegmentKey,
    normalizeData,
    hoverLinePlugin,
} from './mmi-chart-shared.js';

(function () {
    const panel = document.querySelector('[data-mmi-confidence-chart]');
    if (!panel) return;

    const canvas = panel.querySelector('canvas');
    const timeButtons = panel.querySelectorAll('[data-time-filter]');

    let rawData = [];
    let rowMap = {};
    let activeTime = '2yr';
    let activeFilters = {
        region: '',
        band: '',
        industry: '',
        ownershipType: '',
    };
    let chartInstance = null;

    // ── Data processing ──────────────────────────────────────────────────────

    function computeChartData() {
        const { visibleCombos } = getVisibleCombos(rawData, activeTime);
        const segmentKey = getActiveSegmentKey(activeFilters);

        const get = (row, suffix) => {
            if (!row) return null;
            const key = segmentKey ? `${segmentKey}:${suffix}` : suffix;
            const v = parseFloat(row[key]);
            return isNaN(v) ? null : v;
        };

        const labels = visibleCombos.map(k => { const [y, q] = k.split('|'); return `${y} Q${q}`; });
        const globalValues = visibleCombos.map(k => get(rowMap[k], 'Global'));
        const usValues = visibleCombos.map(k => get(rowMap[k], 'National'));
        const localValues = visibleCombos.map(k => get(rowMap[k], 'Local'));

        return { labels, globalValues, usValues, localValues };
    }

    // ── Chart rendering ──────────────────────────────────────────────────────

    const COLORS = {
        global: '#5A4198',
        us: '#00AF95',
        local: '#004769',
    };

    function makeDataset(label, data, color) {
        return {
            label,
            data,
            borderColor: color,
            backgroundColor: 'transparent',
            pointBackgroundColor: color,
            pointRadius: 3,
            pointHoverRadius: 5,
            fill: false,
            tension: 0.3,
            borderWidth: 2,
            spanGaps: true,
        };
    }

    function renderChart() {
        const { labels, globalValues, usValues, localValues } = computeChartData();

        if (chartInstance) {
            chartInstance.data.labels = labels;
            chartInstance.data.datasets[0].data = globalValues;
            chartInstance.data.datasets[1].data = usValues;
            chartInstance.data.datasets[2].data = localValues;
            chartInstance.update();
            return;
        }

        chartInstance = new Chart(canvas, {
            type: 'line',
            plugins: [hoverLinePlugin],
            data: {
                labels,
                datasets: [
                    makeDataset('Global Confidence', globalValues, COLORS.global),
                    makeDataset('US Confidence', usValues, COLORS.us),
                    makeDataset('Local Confidence', localValues, COLORS.local),
                ],
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                animation: { duration: 500, easing: 'easeInOutQuart' },
                plugins: {
                    legend: {
                        display: true,
                        position: 'bottom',
                        align: 'start',
                        title: {
                            display: true,
                            text: ' ',
                            padding: { bottom: 40 },
                        },
                        labels: {
                            usePointStyle: true,
                            pointStyle: 'circle',
                            boxHeight: 8,
                            padding: 20,
                        },
                    },
                    tooltip: {
                        backgroundColor: '#ffffff',
                        borderColor: '#082B41',
                        borderWidth: 1,
                        padding: 20,
                        usePointStyle: true,
                        titleColor: '#082B41',
                        bodyColor: '#082B41',
                        footerColor: '#082B41',
                        callbacks: {
                            labelPointStyle: () => ({
                                pointStyle: 'circle',
                                rotation: 0,
                            }),
                            label: ctx => {
                                const v = ctx.parsed.y;
                                return v !== null
                                    ? `${ctx.dataset.label}: ${v}`
                                    : `${ctx.dataset.label}: No data`;
                            },
                        },
                    },
                },
                scales: {
                    x: {
                        grid: { display: false },
                        ticks: {
                            autoSkip: true,
                            maxRotation: 0,
                            minRotation: 0,
                            font: { size: 11 },
                        },
                    },
                    y: {
                        border: { display: false },
                        grid: {
                            color: 'rgba(0,0,0,0.07)',
                        },
                        ticks: {
                            font: { size: 11 },
                        },
                    },
                },
            },
        });
    }

    // ── Inline data ──────────────────────────────────────────────────────────

    const dataScript = document.getElementById('mmi-data-confidence');
    if (!dataScript) return;
    try {
        rawData = normalizeData(JSON.parse(dataScript.textContent));
    } catch (err) {
        console.error('[mmi-confidence-chart] Failed to parse inline data:', err);
        return;
    }
    if (!rawData.length) return;
    rowMap = Object.fromEntries(rawData.map(d => [`${d.Year}|${d.Quarter}`, d]));
    buildFilterSelects(panel, rawData);
    attachTimeListeners(timeButtons, time => { activeTime = time; renderChart(); });
    attachFilterListeners(panel, activeFilters, renderChart);
    renderChart();
})();
